import { Box, Flex, Heading, ListItem, OrderedList, Text } from "@chakra-ui/react";
import { useContext, useRef, useState } from "react";

import { useRect } from "@nens/react/hooks";

import { MapResult } from "@/api";
import { TbiMarkdown } from "@/components/tbi-markdown";
import { ReportLegend } from "@/features/report-legend/components/report-legend";
import { REPORT_MAP_PADDING_Y, ReportMap } from "@/features/report-map/components/report-map";
import { ProjectContext } from "@/stores/project-provider";
import { displayNumber } from "@/utils/get-display-number";
import { getLabelColor, getLabelLabel } from "@/utils/get-label-utils";

import { PAGE_WIDTH_PX, REPORT_BLOCK_GAP } from "../consts/sizes";

import { ReportBlock } from "./report-block";
import { ReportPage, ReportPagesProvider } from "./report-page";
import { ReportPageLanding } from "./report-page-landing";
import { ReportPageSources } from "./report-page-sources";
import { ReportStatistics } from "./report-statistics";

export function ReportSelf() {
  const { project } = useContext(ProjectContext);
  // The biggest legend pixel size is used to set the bottom padding of the map,
  // so that the legend doesn't overlap with the map.
  // We need to get the biggest legend pixel size of all legends in the report,
  // because we want to use the same padding for all maps.
  const [biggestLegendPixelSize, _setBiggestLegendPixelSize] = useState(REPORT_MAP_PADDING_Y);

  const setBiggestLegendPixelSize = (size: number) => {
    _setBiggestLegendPixelSize((prev) => Math.max(prev, size));
  };

  return (
    <Flex
      flexDir="column"
      sx={{
        p: 10,
        "@media print": {
          p: 0,
        },
      }}
    >
      <Flex
        mx="auto"
        w={PAGE_WIDTH_PX + "px"}
        pb={10}
        sx={{
          "@media print": {
            display: "none",
          },
        }}
        justifyContent="space-between"
      >
        <Box>
          <Text>Om op te slaan als PDF, volg deze stappen:</Text>
          <OrderedList>
            <ListItem>Druk op Ctrl + P</ListItem>
            <ListItem>Selecteer "Opslaan als PDF" bij "Bestemming"</ListItem>
            <ListItem>Klik op Opslaan / Afdrukken</ListItem>
          </OrderedList>
        </Box>
        <Box>
          <Text>To save as PDF, please follow these steps:</Text>
          <OrderedList>
            <ListItem>Press Ctrl + P</ListItem>
            <ListItem>Select "Save as PDF" in "Destination"</ListItem>
            <ListItem>Click Save / Print</ListItem>
          </OrderedList>
        </Box>
      </Flex>
      <Box
        fontSize="small"
        mx="auto"
        w={PAGE_WIDTH_PX + "px"}
        sx={{
          "@media print": {
            "*": {
              WebkitPrintColorAdjust: "exact",
              printColorAdjust: "exact",
            },
          },
        }}
      >
        <ReportPagesProvider>
          <ReportPageLanding />
          <Box>
            {project.themes.map((theme) => {
              const { name, description, maps } = theme;

              if (!maps) return null;

              // Group maps in batches of 2
              const mapBatches: (typeof maps)[] = [];
              for (let i = 0; i < maps.length; i += 2) {
                mapBatches.push(maps.slice(i, i + 2));
              }

              return mapBatches.map((mapBatch, batchIndex) => (
                <ReportPage key={`${theme.id}-batch-${batchIndex}`}>
                  {batchIndex === 0 && (
                    <>
                      <Heading as="h2" size="h2">
                        {name}
                      </Heading>
                      <Text>{description}</Text>
                    </>
                  )}

                  {mapBatch.map((mapResult) => (
                    <RenderReportBlock
                      key={mapResult.id}
                      mapResult={mapResult}
                      biggestLegendPixelSize={biggestLegendPixelSize}
                      setBiggestLegendPixelSize={setBiggestLegendPixelSize}
                    />
                  ))}
                </ReportPage>
              ));
            })}
          </Box>
          <ReportPageSources />
        </ReportPagesProvider>
      </Box>
    </Flex>
  );
}

type RenderReportBlockProps = {
  mapResult: MapResult;
  biggestLegendPixelSize: number;
  setBiggestLegendPixelSize: (size: number) => void;
};

// Label values that are integers from 1 to 10 with ".0", ".00", ",0" or ",00"
const LABEL_VALUE_INTEGERS = Array.from({ length: 10 }, (_, i) => [
  i + ".0",
  i + ".00",
  i + ",0",
  i + ",00",
]).flat();

const getLabelValue = ({ labelValue, labelPrimair, labelCategories }: MapResult) => {
  if (!labelValue) return null;

  // If label is an integer, get description from categories
  if (LABEL_VALUE_INTEGERS.includes(labelValue)) {
    return (labelCategories ?? []).find((l) => l.label === labelPrimair)?.description;
  }

  // Is number
  if (!isNaN(+labelValue)) {
    return displayNumber(+labelValue, { toFixed: 2 });
  }

  return labelValue;
};

const RenderReportBlock = ({
  mapResult,
  biggestLegendPixelSize,
  setBiggestLegendPixelSize,
}: RenderReportBlockProps) => {
  const {
    id,
    name,
    description,
    labelCategories,
    labelPrimair,
    labelDescription,
    showLegend,
    labelType,
    percentages,
    statistics,
    statisticTitle,
  } = mapResult;
  const hasLabelCategories = labelCategories && labelCategories.length > 0;
  const labelColor = labelPrimair && getLabelColor(labelCategories, labelPrimair);
  const labelValue = getLabelValue(mapResult);
  const actuallyShowLegend = showLegend || !!percentages?.length;
  const legendRef = useRef<HTMLDivElement | null>(null);
  const { height } = useRect(legendRef);
  setBiggestLegendPixelSize(height);

  return (
    <ReportBlock>
      <ReportBlock.Small p={0} gap={REPORT_BLOCK_GAP} bg={undefined} title={undefined}>
        <ReportBlock.Medium
          w="full"
          title={hasLabelCategories ? "Label" : "Legenda"}
          flexDir="column"
          flexGrow={1}
        >
          {(() => {
            if (hasLabelCategories)
              return (
                <>
                  <Box>
                    {getLabelLabel(labelType)}
                    <ReportBlock.LabelValue {...{ labelColor, labelPrimair }} />
                  </Box>
                  <Box>
                    <ReportBlock.Title title="Score" size="sm" />
                    {labelDescription}
                  </Box>
                  <ReportBlock.LabelCategories {...{ labelCategories }} />
                </>
              );

            if (actuallyShowLegend) return <ReportLegend mapResult={mapResult} />;

            return null;
          })()}
        </ReportBlock.Medium>

        {statistics && (
          <ReportBlock.Small title={statisticTitle ?? "Statistieken"} w="full">
            <ReportStatistics statistics={statistics} />
          </ReportBlock.Small>
        )}

        {labelValue && (
          <ReportBlock.Small title={statisticTitle ?? "Kaart waarde"} w="full">
            {labelValue}
          </ReportBlock.Small>
        )}
      </ReportBlock.Small>
      <ReportBlock.Map title={name} isMap>
        <ReportMap
          mapBorderBottom={biggestLegendPixelSize + REPORT_MAP_PADDING_Y}
          mapResult={mapResult}
          mapId={id}
        >
          {actuallyShowLegend && hasLabelCategories && (
            <Box ref={legendRef} bg="brand.bg" p={2} position="absolute" bottom={0} left={0}>
              <ReportBlock.Title title="Legenda" size="sm" />
              <ReportLegend isAboveMap mapResult={mapResult} w="150px" pt={2} />
            </Box>
          )}
        </ReportMap>
      </ReportBlock.Map>
      <ReportBlock.Medium title="Toelichting op de kaart" flexDir="column">
        <TbiMarkdown markdown={description} />
      </ReportBlock.Medium>
    </ReportBlock>
  );
};
